import { useState } from "react";
import PropTypes from 'prop-types';
import { supabase } from "../config/supabase";
import { useAuth } from "../hooks/AuthContext";

// ─── BugReportButton ─────────────────────────────────────────────────────────
// Floating ⚑ button fixed to bottom-right of every page.
// Opens a modal pre-filled with current page, user email, and timestamp.
// Submits to Supabase `bug_reports` table.
// Props:
//   currentPage  — string, the current view/page name (e.g. "dashboard")
//   theme        — T object from App.js (T.bg, T.surface, T.border, T.text, etc.)
// ─────────────────────────────────────────────────────────────────────────────

export function BugReportButton({ currentPage, theme }) {
  const { user, orgId } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const T = theme || {};

  // ── Colors ──────────────────────────────────────────────────────────────
  const PRIMARY     = "#22D3EE";
  const BG          = T.bg      || "#0A1720";
  const SURFACE     = T.surface || "#0F2230";
  const BORDER      = T.border  || "#142D3E";
  const TEXT        = T.textBold || "#FFFFFF";
  const TEXT_SEC    = T.textSec  || "#B5B9BC";
  const RED         = "#DC2626";

  // ── Derived display values ───────────────────────────────────────────────
  const pageName    = currentPage
    ? currentPage.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : "Unknown";
  const timestamp   = new Date().toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true
  });

  // ── Submit ───────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!message.trim()) return;
    setStatus("submitting");
    try {
      const { error } = await supabase.from("bug_reports").insert({
        org_id:  orgId  || null,
        user_id: user?.id || null,
        page:    currentPage || "unknown",
        message: message.trim(),
        status:  "open",
      });
      if (error) throw error;
      setStatus("success");
      setTimeout(() => {
        setOpen(false);
        setMessage("");
        setStatus("idle");
      }, 1800);
    } catch (err) {
      console.error("Bug report error:", err);
      setStatus("error");
    }
  }

  function handleClose() {
    if (status === "submitting") return;
    setOpen(false);
    setMessage("");
    setStatus("idle");
  }

  // ── Character limit ──────────────────────────────────────────────────────
  const MAX_CHARS   = 1000;
  const charsLeft   = MAX_CHARS - message.length;

  return (
    <>
      {/* ── Floating trigger button ─────────────────────────────────────── */}
      <button
        onClick={() => setOpen(true)}
        title="Report a bug"
        style={{
          position:     "fixed",
          bottom:       24,
          right:        24,
          zIndex:       9000,
          width:        44,
          height:       44,
          borderRadius: "50%",
          background:   "#142D3E",
          border:       `1px solid ${BORDER}`,
          cursor:       "pointer",
          display:      "flex",
          alignItems:   "center",
          justifyContent: "center",
          fontSize:     18,
          color:        TEXT_SEC,
          boxShadow:    "0 2px 8px rgba(0,0,0,0.3)",
          transition:   "background 0.15s, color 0.15s, border-color 0.15s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background    = PRIMARY + "22";
          e.currentTarget.style.borderColor   = PRIMARY;
          e.currentTarget.style.color         = PRIMARY;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background    = "#142D3E";
          e.currentTarget.style.borderColor   = BORDER;
          e.currentTarget.style.color         = TEXT_SEC;
        }}
      >
        ⚑
      </button>

      {/* ── Modal overlay ───────────────────────────────────────────────── */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position:        "fixed",
            inset:           0,
            zIndex:          9100,
            background:      "rgba(0,0,0,0.7)",
            display:         "flex",
            alignItems:      "flex-end",
            justifyContent:  "flex-end",
            padding:         "0 24px 80px 0",
          }}
        >
          {/* Modal card */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:   SURFACE,
              border:       `1px solid ${BORDER}`,
              borderRadius: 12,
              width:        360,
              boxShadow:    "0 4px 16px rgba(0,0,0,0.3)",
              overflow:     "hidden",
            }}
          >
            {/* Header */}
            <div style={{
              display:        "flex",
              alignItems:     "center",
              justifyContent: "space-between",
              padding:        "14px 18px",
              borderBottom:   `1px solid ${BORDER}`,
              background:     BG,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16, color: PRIMARY }}>⚑</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: TEXT, fontFamily: "'Inter',sans-serif" }}>
                  Report a Bug
                </span>
              </div>
              <button aria-label="Close"
                onClick={handleClose}
                style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_SEC, fontSize: 18, lineHeight: 1, padding: 2 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 18px 18px", fontFamily: "'Inter',sans-serif" }}>

              {status === "success" ? (
                /* ── Success state ── */
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>✓</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 4 }}>
                    Report submitted!
                  </div>
                  <div style={{ fontSize: 12, color: TEXT_SEC }}>
                    Thanks — we'll look into it.
                  </div>
                </div>
              ) : (
                <>
                  {/* Pre-filled metadata */}
                  <div style={{
                    background:   BG,
                    border:       `1px solid ${BORDER}`,
                    borderRadius: 8,
                    padding:      "10px 12px",
                    marginBottom: 14,
                    fontSize:     12,
                    color:        TEXT_SEC,
                    lineHeight:   1.7,
                  }}>
                    <div><span style={{ color: TEXT_SEC, fontWeight: 600 }}>Page: </span>
                      <span style={{ color: TEXT }}>{pageName}</span>
                    </div>
                    <div><span style={{ color: TEXT_SEC, fontWeight: 600 }}>User: </span>
                      <span style={{ color: TEXT }}>{user?.email || "Unknown"}</span>
                    </div>
                    <div><span style={{ color: TEXT_SEC, fontWeight: 600 }}>Time: </span>
                      <span style={{ color: TEXT }}>{timestamp}</span>
                    </div>
                  </div>

                  {/* Message field */}
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: TEXT_SEC, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
                    What happened?
                  </label>
                  <textarea
                    autoFocus
                    value={message}
                    onChange={e => setMessage(e.target.value.slice(0, MAX_CHARS))}
                    placeholder="Describe the bug, what you expected to happen, and any steps to reproduce it…"
                    rows={5}
                    style={{
                      width:        "100%",
                      background:   BG,
                      border:       `1px solid ${BORDER}`,
                      borderRadius: 8,
                      padding:      "10px 12px",
                      fontSize:     13,
                      fontFamily:   "'Inter',sans-serif",
                      color:        TEXT,
                      resize:       "vertical",
                      outline:      "none",
                      lineHeight:   1.5,
                      boxSizing:    "border-box",
                    }}
                    onFocus={e => { e.target.style.borderColor = PRIMARY; }}
                    onBlur={e  => { e.target.style.borderColor = BORDER; }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, marginBottom: 14 }}>
                    <span style={{ fontSize: 11, color: charsLeft < 100 ? "#F59E0B" : TEXT_SEC }}>
                      {charsLeft} chars remaining
                    </span>
                    {status === "error" && (
                      <span style={{ fontSize: 11, color: RED }}>
                        Submission failed — try again
                      </span>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={!message.trim() || status === "submitting"}
                    style={{
                      width:        "100%",
                      padding:      "10px 0",
                      background:   (!message.trim() || status === "submitting") ? BORDER : PRIMARY,
                      color:        (!message.trim() || status === "submitting") ? TEXT_SEC : "#0A1720",
                      border:       "none",
                      borderRadius: 8,
                      fontSize:     13,
                      fontWeight:   700,
                      fontFamily:   "'Inter',sans-serif",
                      cursor:       (!message.trim() || status === "submitting") ? "not-allowed" : "pointer",
                      transition:   "background 0.15s",
                    }}
                  >
                    {status === "submitting" ? "Submitting…" : "Submit Report"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

BugReportButton.propTypes = {
  theme: PropTypes.object,
};
